import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { HttpService } from '@Services/index';
import { getBusinessOwnerData, syncBusinessUbosToStore } from './util';
import type { UboResponse } from '@Store/index.ts';

export interface GetBusinessOwnersParams {
  businessExternalId: string;
  isEmbedded: boolean;
  customerId: string;
}
export const getBusinessOwners = async ({
  businessExternalId,
  isEmbedded,
  customerId,
}: GetBusinessOwnersParams) => {
  const url = isEmbedded
    ? `v3/secureonboarding/partner/user/${customerId}/business/${businessExternalId}/ubo`
    : `v2/secureonboarding/user/${customerId}/business/${businessExternalId}/ubo`;
  const { data } = await HttpService.get<{ uboResponseList: UboResponse[] }>(url);
  return data?.uboResponseList;
};

export const getBusinessOwnersQueryKey = ({
  businessExternalId,
  isEmbedded,
  customerId,
}: GetBusinessOwnersParams) =>
  ['businessOwners', businessExternalId, isEmbedded, customerId] as const;

export const getBusinessOwnersQueryDataOptions = ({
  businessExternalId,
  isEmbedded,
  customerId,
}: GetBusinessOwnersParams) =>
  queryOptions({
    queryKey: getBusinessOwnersQueryKey({ businessExternalId, isEmbedded, customerId }),
    queryFn: async ({
      queryKey: [_, businessExternalIdFromQueryKey, isEmbeddedFromQueryKey, customerIdFromQueryKey],
    }) => {
      if (!businessExternalIdFromQueryKey) {
        return [];
      }
      const businessOwners = await getBusinessOwners({
        businessExternalId: businessExternalIdFromQueryKey,
        isEmbedded: isEmbeddedFromQueryKey,
        customerId: customerIdFromQueryKey,
      });
      syncBusinessUbosToStore(businessOwners);
      return businessOwners;
    },
    refetchOnMount: true,
  });

export const deleteBusinessOwner = async (uboId: string) => {
  const { businessExternalId, isEmbedded, customerId } = getBusinessOwnerData();

  const url = isEmbedded
    ? `v3/secureonboarding/partner/user/${customerId}/business/${businessExternalId}/ubo/${uboId}`
    : `v2/secureonboarding/user/${customerId}/business/${businessExternalId}/ubo/${uboId}`;
  await HttpService.delete(url);
};

interface UseDeleteBusinessOwnerParams {
  businessExternalId: string;
  isEmbedded: boolean;
  customerId: string;
}
export const useDeleteBusinessOwner = ({
  businessExternalId,
  isEmbedded,
  customerId,
}: UseDeleteBusinessOwnerParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteBusinessOwner,
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: getBusinessOwnersQueryKey({ businessExternalId, isEmbedded, customerId }),
      });
    },
  });
};
interface OwnershipPercentUpdate {
  customerExternalId: string;
  ownershipPercent: number;
}

interface BulkUpdateOwnershipPercentParams {
  updates: OwnershipPercentUpdate[];
  businessExternalId: string;
  isEmbedded: boolean;
  customerId: string;
}

export const bulkUpdateOwnershipPercent = async ({
  updates,
  businessExternalId,
  isEmbedded,
  customerId,
}: BulkUpdateOwnershipPercentParams) => {
  const url = isEmbedded
    ? `v3/secureonboarding/partner/user/${customerId}/business/${businessExternalId}/ownershipPercent/bulk`
    : `v3/secureonboarding/user/${customerId}/business/${businessExternalId}/ownershipPercent/bulk`;
  await HttpService.post(url, { updates });
};

interface UseBulkUpdateOwnershipPercentParams {
  businessExternalId: string;
  isEmbedded: boolean;
  customerId: string;
}

export const useBulkUpdateOwnershipPercent = ({
  businessExternalId,
  isEmbedded,
  customerId,
}: UseBulkUpdateOwnershipPercentParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: OwnershipPercentUpdate[]) =>
      bulkUpdateOwnershipPercent({
        businessExternalId,
        isEmbedded,
        customerId,
        updates,
      }),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: getBusinessOwnersQueryKey({ businessExternalId, isEmbedded, customerId }),
      });
    },
  });
};
